/**
 * Расширение окна товара.
 *
 * Окно товара одно на всех, а у пряников и наборов есть то, чего у обычного
 * товара нет: состав, производственные файлы, свои кнопки «Что дальше». Эти
 * части живут в `features/gingerbread-catalog` и подключаются сюда расширением,
 * а не импортом: окно товара не должно знать про каталог пряников, иначе
 * зависимость замкнётся (страница пряников сама открывает это окно).
 */

import type React from 'react'
import type { LucideIcon } from 'lucide-react'
import type { EntityMoreMenuItem } from '../../../../components/entity-view/EntityModalShell'
import type { ChainLink } from '../../../../components/ui/EntityChain'
import type { IdMetaItem } from '../../../../components/ui/IdMeta'
import type { NextStepAction, NextStepCheck } from '../../../../components/ui/NextStepBar'
import type { CardSidebarStat } from '../../../marketplace-catalog/components/CardSidebar'
import type { ProductPresenceEntry, ProductV2 } from '../../types'

/** Вкладка окна: как у обычных (`extraTabs`), плюс счётчик и метка проблемы. */
export interface ProductWindowTab {
  key: string
  label: string
  icon?: React.ReactNode
  count?: number
  attention?: 'warn' | 'bad'
  render: (product: ProductV2) => React.ReactNode
}

/** Что окно даёт расширению: переключить вкладку и уйти из окна через защиту правок. */
export interface ProductWindowContext {
  setTab: (key: string) => void
  guard: (action: () => void) => void
  /** Присутствие артикула на площадках; null — ещё не загружено. */
  presence: ProductPresenceEntry[] | null
  /**
   * Артикул, который открыт в окне. Известен сразу, а товар приходит
   * после запросов: расширение может начать свою загрузку параллельно, не ждать товара.
   */
  article?: string | null
}

/** Вклад расширения в окно. Всё необязательно: пустой вклад ничего не меняет. */
export interface ProductWindowContribution {
  /** Метка типа в шапке вместо «Товар»: «Пряник», «Набор пряников · PP1». */
  kind?: { label: string; icon?: LucideIcon }
  /** Звенья цепочки перед макетом: «Задание генератора». */
  chainBefore?: ChainLink[]
  /** Дополнительные служебные номера шапки. */
  meta?: IdMetaItem[]
  /**
   * Вкладки расширения. Ключ `composition` встаёт сразу после «Обзора»,
   * `production` заменяет штатное «Производство», остальные — перед «Площадками».
   */
  tabs?: ProductWindowTab[]
  /** Превью вместо первого изображения товара (лист пряника, превью набора). */
  heroUrl?: string | null
  /** Цифры колонки после штатных. */
  stats?: CardSidebarStat[]
  /** Кнопки полосы «Что дальше»: идут раньше штатных, первая становится главной. */
  actions?: NextStepAction[]
  /**
   * Чипы проверок: с тем же ключом заменяют штатные, остальные добавляются.
   * `problem` — формулировка проблемы для вывода одной фразой (у тонов bad и warn).
   */
  checks?: Array<NextStepCheck & { problem?: string }>
  /** Ключи штатных кнопок полосы, которые расширению не нужны: `print`, `create-cards`. */
  hideActions?: string[]
  /** Пункты «Ещё» перед штатным удалением. */
  moreMenu?: EntityMoreMenuItem[]
  /** Диалоги расширения (смонтированы всегда, открывает их стейт расширения). */
  overlays?: React.ReactNode
}

export interface ProductWindowExtension {
  /**
   * Хук, а не функция: расширению нужны данные (визуалы пряника) и состояние
   * диалогов. Вызывается на каждом рендере окна одним и тем же — ссылка на
   * расширение обязана быть стабильной (константа модуля).
   */
  useContribution: (product: ProductV2 | null, context: ProductWindowContext) => ProductWindowContribution
}

export const EMPTY_CONTRIBUTION: ProductWindowContribution = {}
